import { useAssistantState } from '@assistant-ui/react'
import { ExternalLink } from 'lucide-react'
import { type FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '#app/components/ui/button'
import { trackUIEvent } from '#app/utils/plausible.client'

/**
 * Embed-variant counterpart to NewChatButton. Visually identical (same Button
 * styling and #new-chat-slot portal target) but opens the full assistant in
 * a new tab instead of resetting the in-iframe thread. No confirm dialog —
 * the embed thread is left untouched, so there's nothing to lose.
 *
 * Hidden when the thread is empty: with nothing in the conversation, the
 * affordance has no anchor and just adds visual noise.
 */
export const OpenInAssistantButton: FC = () => {
	const isEmpty = useAssistantState(({ thread }) => thread.isEmpty)
	const [slot, setSlot] = useState<HTMLElement | null>(null)

	useEffect(() => {
		setSlot(document.getElementById('new-chat-slot'))
	}, [])

	if (!slot || isEmpty) return null

	const handleClick = () => {
		trackUIEvent({
			eventName: 'button_click',
			label: 'embed_open_in_assistant',
			location: 'assistent_embed',
		})
		window.open('/', '_blank', 'noopener,noreferrer')
	}

	return createPortal(
		<Button
			variant="outline"
			size="sm"
			onClick={handleClick}
			className="whitespace-nowrap bg-background/80 shadow-sm backdrop-blur dark:bg-background/60"
		>
			<ExternalLink className="mr-2 size-4" aria-hidden="true" />
			<span>Ny konversation</span>
			<span className="sr-only"> (öppnas i ny flik)</span>
		</Button>,
		slot,
	)
}
